/**
 * EmailToneSelector — Warmer / Standard / Formal register selector for the
 * Email Helper (Phase B2).
 *
 * A 3-segment pill group (radiogroup). Default Standard = today's voice, so a
 * CM who never touches this gets the unchanged output. The control is shown
 * ONLY for Care-Management emails: in SPICE mode the page hides it (SPICE
 * already carries its own warmth, so a tone shift would double up). Tone is
 * lifted to the page (like the Care-Management <-> SPICE mode) so it threads
 * to the generate call and into the saved draft's form_input snapshot.
 *
 * Accessibility: a `role="radiogroup"` labelled "Email tone register" with
 * three `role="radio"` buttons; arrow-key/Space/Enter operate natively on the
 * focused button and the active one carries aria-checked. x.6.1 tokens; zero
 * raw hexes.
 */

import { type ReactNode } from 'react';

export type EmailTone = 'warmer' | 'standard' | 'formal';

const OPTIONS: ReadonlyArray<{ value: EmailTone; label: string; hint: string }> = [
  { value: 'warmer', label: 'Warmer', hint: 'A touch more warmth and care' },
  { value: 'standard', label: 'Standard', hint: 'The everyday Peace voice' },
  { value: 'formal', label: 'Formal', hint: 'Informative and efficient' },
];

export interface EmailToneSelectorProps {
  tone: EmailTone;
  onChange: (tone: EmailTone) => void;
  disabled?: boolean;
}

export function EmailToneSelector({
  tone,
  onChange,
  disabled = false,
}: EmailToneSelectorProps): ReactNode {
  const active = OPTIONS.find((o) => o.value === tone) ?? OPTIONS[1]!;
  return (
    <div>
      <span className="block text-[13px] font-semibold text-sage-800">Tone</span>
      <div
        role="radiogroup"
        aria-label="Email tone register"
        data-testid="email-tone-selector"
        className="mt-1.5 grid grid-cols-3 gap-1 rounded-full bg-sand p-1 border border-sage-300"
      >
        {OPTIONS.map((opt) => {
          const selected = opt.value === tone;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              data-testid={`email-tone-${opt.value}`}
              className={
                'min-h-9 rounded-full px-3 text-sm font-semibold transition-colors duration-[var(--dur-base)] ease-quiet focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 disabled:opacity-50 ' +
                (selected ? 'bg-sage-800 text-cream shadow-sh1' : 'text-sage-800 hover:text-forest-700')
              }
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-ink-soft" data-testid="email-tone-hint">
        {active.hint}
      </p>
    </div>
  );
}
